import ClearIcon from "@mui/icons-material/Clear";
import Logger from "../../../services/Logger";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import ReplayIcon from "@mui/icons-material/Replay";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import WordsService from "../../../services/DatamuseService";

export default function DatamuseAssociation({ callback, searchTerm }) {
    const [word, setWord] = useState("");
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState();

    // Auto-search when external searchTerm changes
    useEffect(() => {
        if (searchTerm && searchTerm.trim() !== "") {
            setWord(searchTerm);
            performSearch(searchTerm);
        }
    }, [searchTerm]);

    const performSearch = (searchWord) => {
        const wordToSearch = searchWord || word;
        if (!wordToSearch.trim()) return;

        setError(null);
        setResults([]);
        setLoading(true);

        WordsService.associationSearch(wordToSearch)
            .then((r) => {
                setLoading(false);
                if (callback) {
                    setMessage(r.length + " words have been found.");
                    setResults(r);
                    callback(r);
                } else {
                    setMessage();
                    setResults(r);
                }
            })
            .catch((err) => {
                Logger.error("Error in association search:", err);
                setLoading(false);
                
                // Check if it's a 504 Gateway Timeout error
                if (err.status === 504 || err.message?.includes('504') || err.message?.includes('Gateway Timeout')) {
                    setError("504 Gateway Timeout: The server took too long to respond. Please try again.");
                } else if (err.message?.includes('Network') || err.message?.includes('fetch') || err.message?.includes('Failed to fetch')) {
                    setError("Network error: Unable to connect to the server. Please check your connection and try again.");
                } else if (err.status) {
                    setError(`Error ${err.status}: ${err.statusText || err.message || "An error occurred while searching for words."}`);
                } else {
                    setError(`Error: ${err.message || "An error occurred while searching for words."}`);
                }
                setMessage();
                if (callback) {
                    callback([]);
                }
            });
    };

    const handleFindWord = () => {
        performSearch();
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            handleFindWord();
        }
    };

    const handleRemoveItem = (wordToRemove) => {
        setResults(results.filter(result => result.word !== wordToRemove));
    };

    const handleClear = () => {
        setWord("");
        setResults([]);
        setMessage("");
        setError(null);
        setLoading(false);
        if (callback) {
            callback([]);
        }
    };

    return (
        <Paper elevation={2} sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, flexGrow: 1, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <Typography variant="subtitle1">Association Search</Typography>
                <Tooltip title="Search for words that are semantically related to the input word">
                    <InfoIcon sx={{ ml: 0.5, fontSize: 16, color: "text.secondary", cursor: "help" }} />
                </Tooltip>
            </Box>
            <Box sx={{ display: "flex", width: "100%", alignItems: "center" }}>
                <TextField
                    label="Enter a word"
                    value={word}
                    onChange={(e) => setWord(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{ mr: 2 }}
                    size={"small"}
                    // helperText="ex) test"
                    fullWidth
                />
                <IconButton
                    onClick={handleFindWord}
                    sx={{ mr: 1 }}
                    size="small"
                >
                    <ReplayIcon />
                </IconButton>
                <IconButton
                    onClick={handleClear}
                    sx={{ mr: 1 }}
                    size="small"
                    color="error"
                >
                    <ClearIcon />
                </IconButton>
            </Box>
            {error && (
                <Alert severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            )}
            {loading && (
                <Typography variant="caption" color="text.secondary">
                    Searching...
                </Typography>
            )}
            {results.length > 0 && (
                <Box sx={{ width: "100%", maxHeight: 500, overflow: "auto", flexGrow: 1 }}>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        {results.map((result) => (
                            <li key={result.word} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #eee' }}>
                                <span>{result.word} (Score: {result.score})</span>
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemoveItem(result.word)}
                                    sx={{ ml: 1, color: 'text.secondary' }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </li>
                        ))}
                    </ul>
                </Box>
            )}
            {message && !error && <Typography>{message}</Typography>}
        </Paper>
    );
}

